import { Button, ToggleGroup, ToggleGroupItem } from '@monksflow/monks-ui';
import {
  CodeIcon,
  LayoutTemplateIcon,
  MonitorIcon,
  PencilIcon,
  SmartphoneIcon,
  SparklesIcon,
} from 'lucide-react';
import { SectionPickerIframe, type SectionPickerClickPayload } from '@/entities/section-picker';
import type { TemplateViewMode } from '@/features/templates-config';
import { HtmlCodeEditor } from '@/shared/ui/HtmlCodeEditor';
import { useTranslation } from 'react-i18next';

interface TemplateEditorSurfaceProps {
  templateName: string;
  html: string;
  viewMode: TemplateViewMode;
  mobilePreview: boolean;
  onViewModeChange: (mode: TemplateViewMode) => void;
  onMobilePreviewChange: (mobile: boolean) => void;
  onHtmlChange: (html: string) => void;
  onElementClick: (payload: SectionPickerClickPayload) => void;
  onRenameClick: () => void;
  onCustomInstructionsClick: () => void;
  /** Whether this template already has standing instructions, which the button's label reflects. */
  hasCustomInstructions: boolean;
  /** Section to outline in the preview while its row in the contents panel is hovered/focused. */
  highlightedSection?: string | null;
}

/** Mobile preview frame width — the canonical 376px step, standing in for a 375px phone viewport. */
const MOBILE_PREVIEW_WIDTH = 'w-94';

export function TemplateEditorSurface({
  templateName,
  html,
  viewMode,
  mobilePreview,
  onViewModeChange,
  onMobilePreviewChange,
  onHtmlChange,
  onElementClick,
  onRenameClick,
  onCustomInstructionsClick,
  hasCustomInstructions,
  highlightedSection = null,
}: TemplateEditorSurfaceProps) {
  const { t } = useTranslation('admin');

  const isPreview = viewMode === 'preview';

  return (
    <div className="flex min-h-0 flex-1 flex-col gap-4 rounded-xl border border-border bg-background p-4">
      <div className="flex items-center justify-between gap-4">
        <div className="flex min-w-0 items-center gap-2">
          <h1 className="truncate text-lg font-medium text-foreground">{templateName}</h1>
          <Button
            variant="ghost"
            size="icon"
            aria-label={t('templatesConfig.editor.renameTemplate')}
            onClick={onRenameClick}
          >
            <PencilIcon className="size-4" />
          </Button>
          <Button
            variant="outline"
            size="sm"
            className="shrink-0"
            onClick={onCustomInstructionsClick}
          >
            <SparklesIcon className="size-4" />
            {hasCustomInstructions
              ? t('templatesConfig.customInstructions.buttonSet')
              : t('templatesConfig.customInstructions.button')}
          </Button>
        </div>

        {isPreview && (
          /* eslint-disable i18next/no-literal-string -- ToggleGroup option values, not copy */
          <ToggleGroup
            type="single"
            variant="outline"
            value={mobilePreview ? 'mobile' : 'desktop'}
            onValueChange={(value) => value && onMobilePreviewChange(value === 'mobile')}
          >
            <ToggleGroupItem
              value="desktop"
              aria-label={t('templatesConfig.editor.desktopPreview')}
            >
              <MonitorIcon className="size-4" />
            </ToggleGroupItem>
            <ToggleGroupItem value="mobile" aria-label={t('templatesConfig.editor.mobilePreview')}>
              <SmartphoneIcon className="size-4" />
            </ToggleGroupItem>
          </ToggleGroup>
          /* eslint-enable i18next/no-literal-string */
        )}

        {/* eslint-disable i18next/no-literal-string -- ToggleGroup option values, not copy */}
        <ToggleGroup
          type="single"
          variant="outline"
          value={viewMode}
          onValueChange={(value) => value && onViewModeChange(value as TemplateViewMode)}
        >
          <ToggleGroupItem value="code">
            <CodeIcon className="size-4" />
            {t('templatesConfig.editor.code')}
          </ToggleGroupItem>
          <ToggleGroupItem value="preview">
            <LayoutTemplateIcon className="size-4" />
            {t('templatesConfig.editor.preview')}
          </ToggleGroupItem>
        </ToggleGroup>
        {/* eslint-enable i18next/no-literal-string */}
      </div>

      <div className="min-h-0 flex-1 overflow-auto">
        {isPreview ? (
          <div className={mobilePreview ? `mx-auto ${MOBILE_PREVIEW_WIDTH}` : 'w-full'}>
            <SectionPickerIframe
              htmlContent={html}
              onElementClick={onElementClick}
              pickerEnabled
              autoHeight
              showElementLabels
              highlightedSection={highlightedSection}
            />
          </div>
        ) : (
          <HtmlCodeEditor
            value={html}
            onChange={onHtmlChange}
            placeholder={t('templatesConfig.editor.pasteHtmlPlaceholder')}
            className="h-full rounded-lg bg-muted"
          />
        )}
      </div>
    </div>
  );
}
